/**
 * ETQ behaviour layer on top of Horizon. Small, dependency-free enhancements:
 * - size guard: until a size is picked, "Add to bag" asks for the size instead of adding
 *   the first size (the server renders data-etq-needs-size on the button in that state)
 * - instant feedback when a size is picked (button label + EU/US/UK conversion line)
 * - model photos: clicking a photo shows that model's height and size
 * - resize fix: Horizon scrolls the document on mobile but .page-wrapper on desktop; when the window
 *   crosses that point the old document scroll would hide the page (white screen), so move it over
 * - collection "Show more · 24 / 106": replaces Horizon's page numbers, appends the next 24 in place
 * - fly-outs: any [data-etq-drawer-open="id"] opens <dialog id="id"> (size guide, delivery, returns)
 * - back-in-stock: "Email me when it's back" posts to Klaviyo (company WWd9K8, as on the live site)
 * - same-day countdown in the delivery promise (Amsterdam time, weekdays, before the cut-off)
 * - mobile filter row as on live: "Filter & sort | Category ⌄ Colour ⌄ …"; each quick trigger opens
 *   Horizon's filter drawer with that filter already unfolded
 * - smart back link on the PDP: back to the collection the visitor came from (with filters), else
 *   the product's main category as rendered by the server
 * - the signature square: on desktop the pointer becomes the ETQ square (opens up to an outline
 *   over links and buttons); the footer square draws itself when it scrolls into view
 * - "Added to bag!": after a successful add the button turns white and the message slides up
 *   (detected through the header cart count, so it works whatever Horizon's add flow does)
 */

const SIZE_FIELDSET = 'variant-picker[data-template-product-match] fieldset.etq-size';

/** @returns {HTMLFieldSetElement | null} */
function mainSizeFieldset() {
  return document.querySelector(SIZE_FIELDSET);
}

/** @param {HTMLFieldSetElement} fieldset */
function askForSize(fieldset) {
  fieldset.classList.remove('etq-size--attention');
  // Restart the animation when the visitor clicks twice in a row
  void fieldset.offsetWidth;
  fieldset.classList.add('etq-size--attention');

  for (const hint of document.querySelectorAll('.etq-size-hint')) {
    hint.hidden = false;
  }

  const rect = fieldset.getBoundingClientRect();
  if (rect.top < 80 || rect.bottom > window.innerHeight) {
    fieldset.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
  fieldset.querySelector('input:not([aria-disabled="true"])')?.focus({ preventScroll: true });
}

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[name="add"][data-etq-needs-size]');
    if (!button) return;

    const fieldset = mainSizeFieldset();
    if (!fieldset || fieldset.querySelector('input:checked')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    // The size button (as on the old site): open the size sheet, the bag add follows the pick (assets/etq-pdp.js)
    if (fieldset.classList.contains('etq-size--select') && document.getElementById('etq-size-sheet')) {
      document.dispatchEvent(new CustomEvent('etq:size-sheet-add'));
      return;
    }
    askForSize(fieldset);
  },
  true
);

document.addEventListener('change', (event) => {
  if (!(event.target instanceof HTMLInputElement)) return;
  const fieldset = event.target.closest('fieldset.etq-size');
  if (!fieldset) return;

  fieldset.classList.remove('etq-size--attention');
  for (const hint of document.querySelectorAll('.etq-size-hint')) {
    hint.hidden = true;
  }

  // The server re-renders the button for the chosen size; switch the label right away so the
  // click feels instant
  const addLabel = document.documentElement.dataset.etqAddLabel;
  for (const button of document.querySelectorAll('button[name="add"][data-etq-needs-size]')) {
    button.removeAttribute('data-etq-needs-size');
    const label = button.querySelector('.etq-atc__label');
    if (label && addLabel) label.textContent = addLabel;
  }

  const value = event.target.value;
  const current = fieldset.querySelector('[data-etq-size-current]');
  if (current) {
    // The visitor's own size system first, as in the size sheet
    const system = current.getAttribute('data-size-system') || '';
    const parts = value.split('|').map((part) => part.trim());
    const own = parts.find((part) => part.includes(system));
    const ordered = own ? [own, ...parts.filter((part) => part !== own)] : parts;
    current.textContent = `${current.getAttribute('data-size-word')}: ${ordered.join(' · ')}`;
  } else if (value.includes('|')) {
    let line = fieldset.querySelector('.etq-size__conversion');
    if (!line) {
      line = document.createElement('p');
      line.className = 'etq-size__conversion';
      fieldset.append(line);
    }
    line.textContent = value.split('|').map((part) => part.trim()).join(' · ');
  }
});

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const pick = event.target.closest('[data-etq-model-line]');
  if (!pick) return;
  const wrapper = pick.closest('[data-etq-models]');
  if (!wrapper) return;

  for (const other of wrapper.querySelectorAll('[data-etq-model-line]')) {
    other.setAttribute('aria-pressed', String(other === pick));
  }
  const current = wrapper.querySelector('[data-etq-model-current]');
  if (current) current.textContent = pick.getAttribute('data-etq-model-line') ?? '';
});

let lastAddButton = null;
let lastAddTime = 0;

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('button[name="add"]');
  if (!button || button.hasAttribute('data-etq-needs-size')) return;
  lastAddButton = button;
  lastAddTime = Date.now();
});

function showAdded(button) {
  const text = document.documentElement.dataset.etqAddedLabel;
  if (!text) return;
  button.setAttribute('data-etq-added-text', text);
  button.setAttribute('data-etq-added', '');
  window.setTimeout(() => button.removeAttribute('data-etq-added'), 3200);
}

function watchCartCount() {
  const counts = document.querySelectorAll('.cart-bubble, [ref="cartBubbleCount"], cart-icon');
  if (!counts.length) return;
  const observer = new MutationObserver(() => {
    if (lastAddButton && Date.now() - lastAddTime < 8000) {
      showAdded(lastAddButton);
      lastAddButton = null;
    }
  });
  for (const count of counts) {
    observer.observe(count, { childList: true, subtree: true, characterData: true, attributes: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchCartCount);
} else {
  watchCartCount();
}

function amsterdamNow() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const get = (type) => parts.find((part) => part.type === type)?.value ?? '';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    weekday: get('weekday'),
    minutes: Number(get('hour')) * 60 + Number(get('minute')),
  };
}

function updateCountdowns() {
  const now = amsterdamNow();
  for (const line of document.querySelectorAll('[data-etq-countdown]')) {
    // MARKET SHIPPING (1 Nov): DK/SE ship until 16:00 on Fridays (data-cutoff-friday)
    const cutoff = now.weekday === 'Fri' && line.dataset.cutoffFriday ? line.dataset.cutoffFriday : line.dataset.cutoff;
    const [h, m] = (cutoff || '').split(':').map(Number);
    const closed = (line.dataset.closed || '').split(',');
    const shippingDay = !['Sat', 'Sun'].includes(now.weekday) && !closed.includes(now.date);
    const left = h * 60 + m - now.minutes;
    if (!shippingDay || Number.isNaN(left) || left <= 0 || left > Number(line.dataset.maxLeft || 12 * 60)) {
      line.hidden = true;
      continue;
    }
    line.textContent = (line.dataset.etqCountdown || '')
      .replace('[h]', String(Math.floor(left / 60)))
      .replace('[m]', String(left % 60).padStart(2, '0'));
    line.hidden = false;
  }
}

updateCountdowns();
window.setInterval(updateCountdowns, 30000);

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('etq-notify__form')) return;
  event.preventDefault();
  const wrapper = form.closest('[data-etq-notify]');
  const email = form.elements.namedItem('email')?.value?.trim();
  const error = wrapper?.querySelector('.etq-notify__error');
  if (!wrapper || !email || !email.includes('@')) {
    if (error) error.hidden = false;
    return;
  }
  const body = {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        profile: { data: { type: 'profile', attributes: { email } } },
        channels: ['EMAIL'],
      },
      relationships: {
        variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${wrapper.dataset.variantId}` } },
      },
    },
  };
  try {
    const response = await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=WWd9K8', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(String(response.status));
    form.hidden = true;
    if (error) error.hidden = true;
    const done = wrapper.querySelector('.etq-notify__done');
    if (done) done.hidden = false;
  } catch {
    if (error) error.hidden = false;
  }
});

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const opener = event.target.closest('[data-etq-drawer-open]');
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('data-etq-drawer-open') ?? '');
    if (dialog instanceof HTMLDialogElement && !dialog.open) {
      event.preventDefault();
      dialog.showModal();
    }
    return;
  }
  // Close button, or a click on the dimmed backdrop (the dialog itself, outside its content)
  const dialog = event.target.closest('dialog.etq-drawer');
  if (!(dialog instanceof HTMLDialogElement)) return;
  if (event.target.closest('[data-etq-drawer-close]') || event.target === dialog) {
    const rect = dialog.getBoundingClientRect();
    const outside = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    if (event.target.closest('[data-etq-drawer-close]') || outside) dialog.close();
  }
});

// Mobile sheet: drag the header down to close (live popup handle behaviour)
document.addEventListener('touchstart', (event) => {
  if (!(event.target instanceof Element) || matchMedia('(min-width: 1024px)').matches) return;
  const head = event.target.closest('.etq-drawer__head');
  const dialog = head?.closest('dialog.etq-drawer');
  if (!(dialog instanceof HTMLDialogElement)) return;
  const startY = event.touches[0].clientY;
  let delta = 0;
  const move = (e) => {
    delta = Math.max(0, e.touches[0].clientY - startY);
    dialog.style.transition = 'none';
    dialog.style.transform = `translateY(${delta}px)`;
  };
  const end = () => {
    dialog.style.transition = '';
    dialog.style.transform = '';
    if (delta > 80) dialog.close();
    head.removeEventListener('touchmove', move);
    head.removeEventListener('touchend', end);
  };
  head.addEventListener('touchmove', move, { passive: true });
  head.addEventListener('touchend', end);
}, { passive: true });

let lastScroller = null;
window.addEventListener('resize', () => {
  const root = document.documentElement;
  const wrapper = document.querySelector('.page-wrapper');
  if (!wrapper) return;
  const rootLocked = getComputedStyle(root).overflow === 'hidden';
  const scroller = rootLocked ? 'wrapper' : 'root';
  if (scroller === lastScroller) return;
  lastScroller = scroller;
  if (rootLocked && root.scrollTop > 0) {
    const top = root.scrollTop;
    root.scrollTop = 0;
    wrapper.scrollTop = top;
  } else if (!rootLocked && wrapper.scrollTop > 0) {
    const top = wrapper.scrollTop;
    wrapper.scrollTop = 0;
    root.scrollTop = top;
  }
});

// Melvin 4 Nov: while more products can follow, the grid ends on a full row. Tiles held back here come first after "Show more".
function fillLastRow(grid, hasMore) {
  const items = [...grid.querySelectorAll('.product-grid__item')];
  for (const item of items) item.classList.remove('etq-row-rest');
  if (!hasMore) return;
  const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').length;
  let cells = 0;
  for (const item of items) cells += item.classList.contains('etq-grid-banner--span-2') ? Math.min(2, columns) : 1;
  let rest = cells % columns;
  for (let i = items.length - 1; i >= 0 && rest > 0; i--) {
    if (items[i].classList.contains('etq-grid-banner')) continue;
    items[i].classList.add('etq-row-rest');
    rest--;
  }
}

function setupLoadMore() {
  const header = document.querySelector('[data-etq-total]');
  const list = document.querySelector('results-list');
  const nav = list?.querySelector('nav.pagination');
  if (!header || !list || !nav || list.querySelector('.etq-more')) return;
  const grid = list.querySelector('.product-grid');
  const sectionId = list.getAttribute('section-id');
  let nextUrl = nav.querySelector('.pagination__item:last-child a.pagination__link--arrow')?.getAttribute('href');
  if (!grid || !nextUrl) return;

  list.classList.add('etq-has-more');
  const wrap = document.createElement('div');
  wrap.className = 'etq-more';
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'etq-more__button';
  const count = () => grid.querySelectorAll('.product-grid__item:not(.etq-grid-banner):not(.etq-row-rest)').length;
  // the total is read each time: assets/etq-filters.js keeps it at the number of products in the grid
  const label = () => `${header.dataset.etqMoreLabel} <span>· ${count()} / ${Number(header.dataset.etqTotal || 0)}</span>`;
  fillLastRow(grid, true);
  button.innerHTML = label();
  wrap.append(button);
  nav.after(wrap);
  window.addEventListener('resize', () => {
    fillLastRow(grid, Boolean(nextUrl));
    if (nextUrl) button.innerHTML = label();
  });

  button.addEventListener('click', async () => {
    if (!nextUrl) return;
    button.disabled = true;
    try {
      const url = new URL(nextUrl, location.origin);
      if (sectionId) url.searchParams.set('section_id', sectionId);
      const html = await (await fetch(url)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      for (const item of doc.querySelectorAll('.product-grid__item')) grid.append(item);
      nextUrl = doc.querySelector('nav.pagination .pagination__item:last-child a.pagination__link--arrow')?.getAttribute('href');
      fillLastRow(grid, Boolean(nextUrl));
      if (nextUrl) {
        button.innerHTML = label();
        button.disabled = false;
      } else {
        wrap.remove();
      }
    } catch {
      button.disabled = false;
    }
  });
}

/** A filter change (or the in-stock render on load) re-renders the section and drops the button: add it back. */
function watchLoadMore() {
  setupLoadMore();
  const section = document.querySelector('results-list')?.closest('.shopify-section');
  if (!section) return;
  let timer;
  new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(setupLoadMore, 60);
  }).observe(section, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchLoadMore);
} else {
  watchLoadMore();
}

/** Live ETQ mobile filter row: the Filter button gets the live sliders icon and label, followed by one
 *  quick trigger per filter. A trigger opens Horizon's drawer and unfolds that filter's panel. */
const SLIDERS_ICON =
  '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path transform="translate(1 2)" fill="currentColor" d="M3.2 5.5728v-1.712H0v-1.6h3.2V.4528h1.6v5.12H3.2Zm3.2-1.712H16v-1.6H6.4v1.6Zm4.8 9.6864v-5.12h1.6v1.808H16v1.6h-3.2v1.712h-1.6Zm-1.6-1.712v-1.6H0v1.6h9.6Z"/></svg>';
const CHEVRON_ICON =
  '<svg width="13" height="9" viewBox="0 0 13 9" fill="none" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="m10.5408.7996-4.404 4.2386L1.718.7997.1294 2.4551l6.0077 5.7452 5.9923-5.7453L10.5408.7996Z"/></svg>';

function setupFilterRow() {
  const toggle = document.querySelector('.facets-toggle__button');
  const wrapper = toggle?.closest('.facets-toggle');
  if (!toggle || !wrapper || wrapper.querySelector('.etq-quick-filters')) return;

  const label = document.documentElement.dataset.etqFilterLabel;
  toggle.innerHTML = `${SLIDERS_ICON}<span>${label || toggle.textContent.trim()}</span>`;

  const drawer = document.querySelector('#filters-drawer') ?? document.querySelector('dialog:has(.facets__panel)');
  const panels = [...(drawer?.querySelectorAll('details.facets__panel') ?? [])];
  if (!panels.length) return;

  const row = document.createElement('div');
  row.className = 'etq-quick-filters';
  for (const panel of panels) {
    if (panel.querySelector("input[name='filter.v.availability']")) continue;
    const name = panel.querySelector('summary')?.firstElementChild?.textContent?.trim() || panel.querySelector('summary')?.textContent?.trim();
    if (!name) continue;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'etq-quick-filters__trigger';
    button.innerHTML = `<span>${name.replace(/\s+/g, ' ')}</span>${CHEVRON_ICON}`;
    button.addEventListener('click', () => {
      toggle.click();
      for (const other of panels) other.open = other === panel;
      requestAnimationFrame(() => panel.scrollIntoView({ block: 'start' }));
    });
    row.append(button);
  }
  toggle.after(row);
}

function watchFilterRow() {
  setupFilterRow();
  // Horizon re-renders the filters after every filter change: rebuild the row when that happens
  const list = document.querySelector('results-list');
  if (list) new MutationObserver(setupFilterRow).observe(list, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchFilterRow);
} else {
  watchFilterRow();
}

/** Smart back link: remember the last collection page; on a PDP opened from it, link back there. */
function setupBacklink() {
  const header = document.querySelector('[data-etq-collection-title]');
  try {
    if (header) {
      sessionStorage.setItem(
        'etq-last-collection',
        JSON.stringify({ url: location.pathname + location.search, title: header.dataset.etqCollectionTitle })
      );
      return;
    }
    const link = document.querySelector('.etq-backlink');
    const last = JSON.parse(sessionStorage.getItem('etq-last-collection') || 'null');
    if (!link || !last || !document.referrer) return;
    const from = new URL(document.referrer);
    if (from.origin !== location.origin || from.pathname !== last.url.split('?')[0]) return;
    link.href = last.url;
    const label = link.querySelector('.etq-backlink__label');
    if (label) label.textContent = last.title;
    // Melvin 4 Nov (customer survey): back to the same spot, like the browser's back button, not the top
    link.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || history.length < 2) return;
      event.preventDefault();
      history.back();
    });
  } catch {
    // storage blocked: the server-rendered main category link stays
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupBacklink);
} else {
  setupBacklink();
}

/** Square cursor (theme setting "Square cursor on desktop"): a black square that trails the pointer
 *  with the ETQ ease and opens to an outline over anything clickable. Fine pointers only, never with
 *  reduced motion; the native cursor comes back over form fields and when the pointer leaves. */
function setupSquareCursor() {
  const root = document.documentElement;
  if (!root.hasAttribute('data-etq-cursor')) return;
  if (!matchMedia('(pointer: fine)').matches || matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const square = document.createElement('div');
  square.className = 'etq-cursor';
  square.setAttribute('aria-hidden', 'true');
  document.body.append(square);
  root.classList.add('etq-cursor-on');

  let x = -100;
  let y = -100;
  let cx = x;
  let cy = y;
  const INTERACTIVE = 'a, button, summary, label, [role="button"], select, .product-card';
  const NATIVE = 'input, textarea, iframe, [contenteditable]';

  addEventListener('pointermove', (event) => {
    x = event.clientX;
    y = event.clientY;
    const target = /** @type {Element} */ (event.target);
    square.classList.toggle('is-hidden', !!target.closest?.(NATIVE));
    square.classList.toggle('is-open', !!target.closest?.(INTERACTIVE));
  }, { passive: true });
  document.addEventListener('pointerleave', () => square.classList.add('is-hidden'));
  addEventListener('pointerdown', () => square.classList.add('is-pressed'));
  addEventListener('pointerup', () => square.classList.remove('is-pressed'));

  const tick = () => {
    cx += (x - cx) * 0.28;
    cy += (y - cy) * 0.28;
    square.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/** Footer square (theme setting): draws its outline, then fills, the first time it is seen. */
function setupSquareDraw() {
  if (!document.documentElement.hasAttribute('data-etq-square-draw')) return;
  const marks = document.querySelectorAll('[data-etq-square]');
  if (!marks.length || !('IntersectionObserver' in window)) return;
  // Observe the square's parent: a clipped (still invisible) square never counts as intersecting
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.querySelector('[data-etq-square]')?.classList.add('is-drawn');
      observer.unobserve(entry.target);
    }
  }, { threshold: 0.5, rootMargin: '0px 0px -15% 0px' });
  for (const mark of marks) {
    mark.classList.add('etq-square-draw');
    if (mark.parentElement) observer.observe(mark.parentElement);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setupSquareCursor();
    setupSquareDraw();
  });
} else {
  setupSquareCursor();
  setupSquareDraw();
}

/** Colour switch material tiles: show the chosen material's colours (no page load until a colour). */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const tile = event.target.closest('[data-etq-material]');
  if (!tile) return;
  const root = tile.closest('.etq-colour-switch');
  if (!root) return;
  const url = tile.getAttribute('data-etq-material-url');
  if (url) {
    location.href = url;
    return;
  }
  const handle = tile.getAttribute('data-etq-material');
  for (const other of root.querySelectorAll('[data-etq-material]')) {
    other.setAttribute('aria-pressed', String(other === tile));
  }
  for (const row of root.querySelectorAll('[data-etq-material-row]')) {
    row.hidden = row.getAttribute('data-etq-material-row') !== handle;
  }
});

/** Mobile gallery: replaces Horizon's "1 / 23" counter with the ETQ position line (a hairline with a
 *  square that travels along it, like the fit bar) and, once per session, a small swipe hint: the first
 *  photo slides a little to the left and back so it is clear there is more to see. */
function setupGalleryIndicator() {
  for (const controls of document.querySelectorAll('.media-gallery__mobile-controls')) {
    const counter = controls.querySelector('.slideshow-controls__counter');
    if (!counter || controls.querySelector('.etq-gallery-line')) continue;
    const total = Number(counter.textContent.split('/').pop().trim()) || 1;
    if (total < 2) continue;
    const line = document.createElement('div');
    line.className = 'etq-gallery-line';
    line.setAttribute('aria-hidden', 'true');
    line.innerHTML = '<span class="etq-gallery-line__square"></span>';
    controls.append(line);
    const current = counter.querySelector('[ref="current"]');
    const place = () => {
      const index = Math.max(1, Number(current?.textContent || 1));
      line.style.setProperty('--etq-pos', String((index - 1) / (total - 1)));
    };
    place();
    if (current) new MutationObserver(place).observe(current, { childList: true, characterData: true, subtree: true });

    // swipe hint, once per session, only when the gallery is on screen and motion is allowed
    const slideshow = controls.closest('slideshow-component');
    const scroller = slideshow && [...slideshow.querySelectorAll('*')].find((el) => el.scrollWidth > el.clientWidth + 10 && /auto|scroll/.test(getComputedStyle(el).overflowX));
    let hinted = false;
    try {
      hinted = sessionStorage.getItem('etq-swipe-hint') === '1';
    } catch {}
    if (!scroller || hinted || matchMedia('(prefers-reduced-motion: reduce)').matches) continue;
    setTimeout(() => {
      if (scroller.scrollLeft > 0) return;
      scroller.scrollBy({ left: 56, behavior: 'smooth' });
      setTimeout(() => scroller.scrollTo({ left: 0, behavior: 'smooth' }), 650);
      try {
        sessionStorage.setItem('etq-swipe-hint', '1');
      } catch {}
    }, 1100);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupGalleryIndicator);
} else {
  setupGalleryIndicator();
}

/** Expected delivery on the PDP ("Verwachte levering: do 5 – vr 6 nov"): ships today when it is a
 *  working day before the country's cut-off (Friday cut-off where it differs), else on the next
 *  working day; then the carrier's transit working days from the live delivery page. */
function addWorkingDays(date, days, closed) {
  const d = new Date(date);
  let left = days;
  while (left > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    const iso = d.toISOString().slice(0, 10);
    if (d.getUTCDay() !== 0 && d.getUTCDay() !== 6 && !closed.includes(iso)) left -= 1;
  }
  return d;
}

function updateEta() {
  const now = amsterdamNow();
  const lang = document.documentElement.lang || 'en';
  const fmt = new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  for (const line of document.querySelectorAll('[data-etq-eta]')) {
    const closed = (line.dataset.closed || '').split(',');
    const [min, max] = (line.dataset.days || '').split('-').map(Number);
    if (!min) continue;
    const isFriday = now.weekday === 'Fri';
    const [h, m] = ((isFriday && line.dataset.cutoffFri) || line.dataset.cutoff || '16:00').split(':').map(Number);
    let ship = new Date(`${now.date}T12:00:00Z`);
    const working = !['Sat', 'Sun'].includes(now.weekday) && !closed.includes(now.date);
    if (!working || now.minutes >= h * 60 + m) ship = addWorkingDays(ship, 1, closed);
    const first = addWorkingDays(ship, min, closed);
    const last = addWorkingDays(ship, max || min, closed);
    const text = first.getTime() === last.getTime() ? fmt.format(first) : `${fmt.format(first)} – ${fmt.format(last)}`;
    line.textContent = (line.dataset.etqEta || '').replace('[date]', text);
    line.hidden = false;
  }
}

function watchEta() {
  updateEta();
  const drawer = document.querySelector('#cart-drawer');
  if (drawer) new MutationObserver(() => {
    if (drawer.querySelector('[data-etq-eta][hidden]')) updateEta();
  }).observe(drawer, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchEta);
} else {
  watchEta();
}

/** Card colour squares (desktop): hovering a square shows that colour's photo on the card; leaving the
 *  row restores it. A click (or a tap on mobile) opens that colour. */
function setupCardSwatches() {
  if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  document.addEventListener('mouseover', (event) => {
    const square = event.target instanceof Element ? event.target.closest('[data-etq-swap]') : null;
    if (!square) return;
    const card = square.closest('product-card, .product-card, .etq-card');
    const img = card?.querySelector('.card-gallery img, .etq-card__media img, img');
    if (!img || !square.dataset.etqSwap) return;
    if (!img.dataset.etqOriginal) {
      img.dataset.etqOriginal = img.currentSrc || img.src;
      img.dataset.etqOriginalSrcset = img.getAttribute('srcset') || '';
    }
    img.removeAttribute('srcset');
    img.src = square.dataset.etqSwap;
  });
  document.addEventListener('mouseout', (event) => {
    const row = event.target instanceof Element ? event.target.closest('.etq-card-meta--colours') : null;
    if (!row || (event.relatedTarget instanceof Element && row.contains(event.relatedTarget))) return;
    const card = row.closest('product-card, .product-card, .etq-card');
    const img = card?.querySelector('img[data-etq-original]');
    if (!img) return;
    img.src = img.dataset.etqOriginal;
    if (img.dataset.etqOriginalSrcset) img.setAttribute('srcset', img.dataset.etqOriginalSrcset);
    delete img.dataset.etqOriginal;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupCardSwatches);
} else {
  setupCardSwatches();
}

/** Gallery videos play by themselves, muted and looping, once they are half on screen (as live) */
function setupGalleryVideos() {
  const items = document.querySelectorAll('media-gallery deferred-media, .product-media deferred-media, deferred-media');
  if (!items.length || !('IntersectionObserver' in window)) return;
  const start = (el, tries = 0) => {
    const video = el.querySelector('video');
    if (video) {
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.play?.().catch(() => {});
      return;
    }
    const button = el.querySelector('button, [role="button"]');
    if (tries > 0) {
      if (tries < 6) setTimeout(() => start(el, tries + 1), 400);
      return;
    }
    if (typeof el.showDeferredMedia === 'function') el.showDeferredMedia();
    else button?.click();
    setTimeout(() => start(el, 1), 400);
  };
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      start(entry.target);
      observer.unobserve(entry.target);
    }
  }, { threshold: 0.5 });
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  for (const el of items) observer.observe(el);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupGalleryVideos);
} else {
  setupGalleryVideos();
}

/** Product photo viewer (replaces Horizon's zoom dialog, which showed every photo full-width under each
 *  other): one photo at a time on the ETQ image grey, with the position line, thumbnails and arrows on
 *  desktop and swipe on mobile. Desktop: a click zooms in and the photo follows the mouse. Mobile: pinch
 *  or double-tap zooms (capped: the photos are 1000 px wide, beyond ~2.5x they turn soft), one finger
 *  pans a zoomed photo, a swipe down closes. Opens from a click on any photo in the product gallery. */
const ZOOM_DESKTOP = 2;
const ZOOM_TAP = 2;
const ZOOM_MAX = 2.5;
const ZOOM_CLOSE_ICON =
  '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path stroke="currentColor" stroke-width="1.5" d="m2 2 14 14M16 2 2 16"/></svg>';
const ZOOM_ARROW_ICON =
  '<svg width="10" height="18" viewBox="0 0 10 18" fill="none" aria-hidden="true"><path stroke="currentColor" stroke-width="1.5" d="m1 1 8 8-8 8"/></svg>';

const escapeHtml = (text) =>
  String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

function zoomUrl(img, width) {
  const raw = img.currentSrc || img.src;
  try {
    const url = new URL(raw, location.href);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return raw;
  }
}

function openZoom(gallery, clicked) {
  // the gallery can hold the same photo twice (desktop grid + mobile slideshow): one slide per photo
  const seen = new Set();
  const sources = [...gallery.querySelectorAll('.product-media-container--image img')].filter((img) => {
    // all photos, also the ones folded away on desktop ("Show all photos"); duplicates removed below
    const key = new URL(img.currentSrc || img.src, location.href).pathname;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (!sources.includes(clicked)) {
    const key = new URL(clicked.currentSrc || clicked.src, location.href).pathname;
    clicked = sources.find((img) => new URL(img.currentSrc || img.src, location.href).pathname === key) || clicked;
  }
  if (!sources.length) return;
  const start = Math.max(0, sources.indexOf(clicked));
  const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const closeLabel = document.querySelector('.close-button[aria-label]')?.getAttribute('aria-label') || 'Close';

  const dialog = document.createElement('dialog');
  dialog.className = 'etq-zoom';
  dialog.innerHTML = `
    <div class="etq-zoom__track">${sources
      .map(
        (img, i) =>
          `<figure class="etq-zoom__slide"><img class="etq-zoom__img" src="${escapeHtml(zoomUrl(img, 2400))}" alt="${escapeHtml(img.alt || '')}" ${
            Math.abs(i - start) > 1 ? 'loading="lazy"' : ''
          } decoding="async" draggable="false"></figure>`
      )
      .join('')}</div>
    ${
      sources.length > 1
        ? `<div class="etq-zoom__thumbs">${sources
            .map((img, i) => `<button type="button" class="etq-zoom__thumb" data-index="${i}" aria-label="${i + 1} / ${sources.length}"><img src="${escapeHtml(zoomUrl(img, 160))}" alt="" loading="lazy"></button>`)
            .join('')}</div>
          <button type="button" class="etq-zoom__arrow etq-zoom__arrow--prev" data-step="-1" aria-label="&larr;">${ZOOM_ARROW_ICON}</button>
          <button type="button" class="etq-zoom__arrow etq-zoom__arrow--next" data-step="1" aria-label="&rarr;">${ZOOM_ARROW_ICON}</button>
          <div class="etq-gallery-line etq-zoom__line" aria-hidden="true"><span class="etq-gallery-line__square"></span></div>`
        : ''
    }
    <button type="button" class="etq-zoom__close" aria-label="${escapeHtml(closeLabel)}">${ZOOM_CLOSE_ICON}</button>`;
  document.body.append(dialog);

  const track = dialog.querySelector('.etq-zoom__track');
  const slides = [...track.children];
  const thumbs = [...dialog.querySelectorAll('.etq-zoom__thumb')];
  const line = dialog.querySelector('.etq-zoom__line');
  let current = start;

  // zoom state per photo: scale and translate, transform-origin top left
  const state = (img) => (img.etqZoom ??= { s: 1, x: 0, y: 0 });
  const local = (img, point) => {
    const box = img.parentElement.getBoundingClientRect();
    return { x: point.clientX - box.left - img.offsetLeft, y: point.clientY - box.top - img.offsetTop };
  };
  const bound = (img, z) => {
    const slide = img.parentElement;
    for (const [axis, size, room, offset] of [
      ['x', img.offsetWidth, slide.clientWidth, img.offsetLeft],
      ['y', img.offsetHeight, slide.clientHeight, img.offsetTop],
    ]) {
      const scaled = size * z.s;
      z[axis] = scaled <= room ? (room - scaled) / 2 - offset : Math.min(-offset, Math.max(room - scaled - offset, z[axis]));
    }
  };
  const apply = (img, animate = false) => {
    const z = state(img);
    img.classList.toggle('is-animating', animate);
    img.style.transform = z.s > 1 ? `translate(${z.x}px, ${z.y}px) scale(${z.s})` : '';
    img.parentElement.classList.toggle('is-zoomed', z.s > 1);
  };
  const zoomAt = (img, point, scale) => {
    const z = state(img);
    const p = local(img, point);
    z.s = scale;
    z.x = p.x * (1 - scale);
    z.y = p.y * (1 - scale);
    bound(img, z);
  };
  const reset = (img, animate = false) => {
    Object.assign(state(img), { s: 1, x: 0, y: 0 });
    apply(img, animate);
  };
  const currentImg = () => slides[current].querySelector('img');

  const mark = () => {
    line?.style.setProperty('--etq-pos', String(slides.length > 1 ? current / (slides.length - 1) : 0));
    thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === current)));
  };
  const go = (index) => {
    const next = Math.max(0, Math.min(slides.length - 1, index));
    reset(currentImg());
    track.scrollTo({ left: next * track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  track.addEventListener(
    'scroll',
    () => {
      const index = Math.round(track.scrollLeft / track.clientWidth);
      if (index === current || !slides[index]) return;
      reset(currentImg());
      current = index;
      mark();
    },
    { passive: true }
  );

  const close = () => dialog.close();
  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('etq-zoom-open');
    dialog.remove();
  });
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') go(current + 1);
    if (event.key === 'ArrowLeft') go(current - 1);
  });
  dialog.querySelector('.etq-zoom__close').addEventListener('click', close);
  for (const arrow of dialog.querySelectorAll('.etq-zoom__arrow')) arrow.addEventListener('click', () => go(current + Number(arrow.dataset.step)));
  for (const thumb of thumbs) thumb.addEventListener('click', () => go(Number(thumb.dataset.index)));

  if (fine) {
    // desktop: click toggles the zoom, the zoomed photo follows the mouse; a click beside the photo closes
    track.addEventListener('click', (event) => {
      const img = event.target instanceof Element ? event.target.closest('.etq-zoom__img') : null;
      if (!img) return close();
      if (state(img).s > 1) return reset(img, true);
      zoomAt(img, event, ZOOM_DESKTOP);
      apply(img, true);
    });
    track.addEventListener('mousemove', (event) => {
      const img = currentImg();
      const z = state(img);
      if (z.s <= 1) return;
      const p = local(img, event);
      z.x = Math.min(1, Math.max(0, p.x / img.offsetWidth)) * img.offsetWidth * (1 - z.s);
      z.y = Math.min(1, Math.max(0, p.y / img.offsetHeight)) * img.offsetHeight * (1 - z.s);
      bound(img, z);
      apply(img, img.classList.contains('is-animating'));
    });
  } else {
    // touch: pinch, double-tap, pan when zoomed, swipe down to close
    let gesture = null;
    let lastTap = { time: 0, x: 0, y: 0 };
    const startPan = (img, touch) => {
      const z = state(img);
      gesture = { type: z.s > 1 ? 'pan' : 'swipe', img, p0: local(img, touch), x0: z.x, y0: z.y, cx: touch.clientX, cy: touch.clientY };
    };
    track.addEventListener(
      'touchstart',
      (event) => {
        const img = currentImg();
        const z = state(img);
        if (event.touches.length === 2) {
          const [a, b] = [local(img, event.touches[0]), local(img, event.touches[1])];
          gesture = { type: 'pinch', img, d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, s0: z.s, x0: z.x, y0: z.y, m0: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
          event.preventDefault();
        } else if (event.touches.length === 1) {
          startPan(img, event.touches[0]);
        }
      },
      { passive: false }
    );
    track.addEventListener(
      'touchmove',
      (event) => {
        if (!gesture) return;
        const { img } = gesture;
        const z = state(img);
        if (gesture.type === 'pinch' && event.touches.length === 2) {
          const [a, b] = [local(img, event.touches[0]), local(img, event.touches[1])];
          const scale = Math.min(ZOOM_MAX, Math.max(1, (gesture.s0 * Math.hypot(a.x - b.x, a.y - b.y)) / gesture.d0));
          const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
          z.s = scale;
          z.x = m.x - (gesture.m0.x - gesture.x0) * (scale / gesture.s0);
          z.y = m.y - (gesture.m0.y - gesture.y0) * (scale / gesture.s0);
          bound(img, z);
          apply(img);
          event.preventDefault();
        } else if (gesture.type === 'pan' && event.touches.length === 1) {
          const p = local(img, event.touches[0]);
          z.x = gesture.x0 + p.x - gesture.p0.x;
          z.y = gesture.y0 + p.y - gesture.p0.y;
          bound(img, z);
          apply(img);
          event.preventDefault();
        }
      },
      { passive: false }
    );
    track.addEventListener('touchend', (event) => {
      if (!gesture) return;
      const { img } = gesture;
      const z = state(img);
      if (gesture.type === 'pinch') {
        if (event.touches.length === 1) return startPan(img, event.touches[0]);
        if (z.s < 1.05) reset(img, true);
        gesture = null;
        return;
      }
      const touch = event.changedTouches[0];
      const dx = touch.clientX - gesture.cx;
      const dy = touch.clientY - gesture.cy;
      if (gesture.type === 'swipe' && dy > 90 && Math.abs(dx) < 60) {
        gesture = null;
        return close();
      }
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
        const now = Date.now();
        if (now - lastTap.time < 300 && Math.hypot(touch.clientX - lastTap.x, touch.clientY - lastTap.y) < 40) {
          if (z.s > 1) reset(img, true);
          else {
            zoomAt(img, touch, ZOOM_TAP);
            apply(img, true);
          }
          lastTap = { time: 0, x: 0, y: 0 };
        } else {
          lastTap = { time: now, x: touch.clientX, y: touch.clientY };
        }
      }
      gesture = null;
    });
  }
  for (const slide of slides) slide.querySelector('img').addEventListener('transitionend', (event) => event.target.classList.remove('is-animating'));

  document.documentElement.classList.add('etq-zoom-open');
  dialog.showModal();
  track.scrollLeft = start * track.clientWidth;
  mark();
}

function setupZoom() {
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const container = target?.closest('.product-media-container--image');
    const gallery = container?.closest('media-gallery');
    if (!gallery || target.closest('a, button, .etq-zoom')) return;
    const img = container.querySelector('img');
    if (img) openZoom(gallery, img);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupZoom);
} else {
  setupZoom();
}

/** Brand film on the PDP: the How it's made video only loads and plays once it is near the screen,
 *  and pauses when it leaves (no 4K video download for visitors who never scroll that far) */
function setupFilmVideos() {
  const videos = document.querySelectorAll('.etq-film__video');
  if (!videos.length || !('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = entry.target;
        if (entry.isIntersecting) {
          video.preload = 'auto';
          video.play?.().catch(() => {});
        } else {
          video.pause();
        }
      }
    },
    { rootMargin: '200px 0px' }
  );
  for (const video of videos) observer.observe(video);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupFilmVideos);
} else {
  setupFilmVideos();
}

/** Recently viewed (PDP): remembers the last products this visitor opened (this browser only) and shows
 *  the others under "You may also like" once there are at least two.
 *  1 Nov (Melvin: "exact zo maken als you may also like"): the row is no longer built here. It is fetched
 *  as HTML from /search?view=etq-recent&q=<handles> (templates/search.etq-recent.json renders
 *  sections/etq-recently-viewed with the same product card block and carousel as "You may also like"),
 *  in the visitor's language and market. */
function setupRecentlyViewed() {
  const box = document.querySelector('[data-etq-recent]');
  if (!box) return;
  const KEY = 'etq-recent';
  const current = box.dataset.current;
  let seen = [];
  try {
    seen = JSON.parse(localStorage.getItem(KEY) || '[]');
  } catch {}
  const others = seen.filter((handle) => handle && handle !== current && /^[a-z0-9-]+$/.test(handle)).slice(0, 8);
  try {
    localStorage.setItem(KEY, JSON.stringify([current, ...others].slice(0, 12)));
  } catch {}
  if (others.length < 2) return;
  const root = window.Shopify?.routes?.root || '/';
  // Only when you get near it: 14 photos (0.6 MB) on the first load of every returning visitor
  const near = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      near.disconnect();
      loadRecentlyViewed(box, root, others);
    },
    { rootMargin: '800px' }
  );
  near.observe(box.closest('.shopify-section') ?? box.parentElement ?? box);
}

/**
 * @param {Element} box
 * @param {string} root
 * @param {string[]} others
 */
function loadRecentlyViewed(box, root, others) {
  fetch(`${root}search?view=etq-recent&type=product&q=${encodeURIComponent(others.join(' '))}`)
    .then((r) => (r.ok ? r.text() : ''))
    .then((html) => {
      const feed = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-etq-recent-feed]');
      if (!feed) return;
      // the section's own <style> tags (resource list, carousel) come along: they are already on the PDP
      // through "You may also like", but keep them in case that section is empty
      box.replaceChildren(...[...feed.ownerDocument.querySelectorAll('style')].map((st) => st.cloneNode(true)), document.importNode(feed, true));
      // component scripts of the card / carousel (inserted <script> tags do not run): load the ones the
      // page does not have yet
      feed.ownerDocument.querySelectorAll('script[src]').forEach((old) => {
        const src = old.getAttribute('src');
        if (document.querySelector(`script[src="${CSS.escape(src)}"]`)) return;
        const script = document.createElement('script');
        script.src = src;
        if (old.type) script.type = old.type;
        document.head.append(script);
      });
      box.hidden = false;
    })
    .catch(() => {});
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupRecentlyViewed);
} else {
  setupRecentlyViewed();
}

/** PDP fly-outs: a fixed "Continue shopping" button at the bottom of every drawer that closes it */
function setupDrawerContinue() {
  const label = window.etqStrings?.continueShopping;
  if (!label) return;
  for (const dialog of document.querySelectorAll('dialog.etq-drawer')) {
    if (dialog.querySelector('.etq-drawer__foot') || dialog.querySelector('.etq-added-sheet__card, [data-etq-no-continue]') || dialog.id.includes('added') || dialog.classList.contains('etq-sheet')) continue; // not on the size and added-to-bag sheets
    const foot = document.createElement('div');
    foot.className = 'etq-drawer__foot';
    foot.innerHTML = `<button type="button" class="etq-drawer__continue" data-etq-drawer-close>${escapeHtml(label)}</button>`;
    dialog.append(foot);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupDrawerContinue);
} else {
  setupDrawerContinue();
}

/** Desktop PDP: long galleries (20+ photos) made the page very long next to an empty right column.
 *  The grid shows the first 6; "Show all photos (n)" unfolds the rest (the viewer always has all). */
function setupGalleryFold() {
  const label = window.etqStrings?.showAllPhotos;
  for (const grid of document.querySelectorAll('media-gallery .media-gallery__grid')) {
    const items = grid.querySelectorAll(':scope > .product-media-container');
    if (!label || items.length <= 7 || grid.nextElementSibling?.classList.contains('etq-gallery-more')) continue;
    grid.classList.add('etq-gallery-folded');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'etq-gallery-more';
    button.textContent = label.replace('[n]', items.length);
    button.addEventListener('click', () => {
      grid.classList.remove('etq-gallery-folded');
      button.remove();
    });
    grid.after(button);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupGalleryFold);
} else {
  setupGalleryFold();
}

/* ===== HEADER SCROLL (31 Oct): the header hides and returns as on live (qa/PROMPT_header_scroll.md) =====
 * As live theme.js: past the bar height + 100px (product page: + the top bar) the header slides away
 * while scrolling down and comes back on any scroll up; above that it always shows. Never while a
 * menu, drawer or search is open. Sets data-etq-hidden on the header section; the look is in the
 * "HEADER SCROLL" block of etq.css. Horizon scrolls .page-wrapper from 990px, the document below. */
function setupHeaderScroll() {
  const section = document.querySelector('.header-section');
  const header = section?.querySelector('#header-component');
  if (!section || !header) return;

  const wide = window.matchMedia('(min-width: 990px)');
  const isProduct = Boolean(document.querySelector('main[data-template^="product"]'));
  let target = null;
  let last = 0;

  const scroller = () =>
    (wide.matches && document.querySelector('.page-wrapper')) || document.scrollingElement || document.documentElement;

  const update = () => {
    const top = scroller().scrollTop;
    const bar = header.querySelector('.header__row--top')?.offsetHeight || header.offsetHeight;
    const extra = isProduct ? document.querySelector('.etq-announcement')?.offsetHeight || 0 : 100;
    const busy = header.querySelector('[aria-expanded="true"], details[open]:not(.menu-drawer details)');
    const down = top > last || (top === last && section.hasAttribute('data-etq-hidden'));
    section.toggleAttribute('data-etq-hidden', !busy && top > bar + extra && down);
    last = top;
  };

  const bind = () => {
    target?.removeEventListener('scroll', update);
    target = (wide.matches && document.querySelector('.page-wrapper')) || window;
    target.addEventListener('scroll', update, { passive: true });
    last = scroller().scrollTop;
    update();
  };

  bind();
  wide.addEventListener('change', bind);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupHeaderScroll);
} else {
  setupHeaderScroll();
}
/* ===== end HEADER SCROLL (31 Oct) ===== */

/* ===== SEARCH MOBILE (2 Nov): the search overlay follows the visual viewport =====
 * Below 750px the search dialog is a fixed full-screen overlay (snippets/etq-search-panel). On iOS Safari the
 * keyboard does not resize the layout viewport (neither 100vh nor 100dvh change), it only shrinks the VISUAL
 * viewport and may pan it. While the dialog is open, `visualViewport` height and offsetTop are copied into
 * --etq-vv-h / --etq-vv-top on #search-modal, so the overlay is exactly the visible area: the input row stays
 * at the top, the black "Show all results" button sits above the keyboard, the page behind never shows.
 * data-etq-kb = keyboard open (visual viewport clearly smaller than the layout viewport, not pinch-zoomed). */
function setupSearchViewport() {
  const modal = document.getElementById('search-modal');
  const vv = window.visualViewport;
  if (!modal || !vv) return;

  const wide = window.matchMedia('(min-width: 750px)');
  let frame = 0;

  const apply = () => {
    frame = 0;
    if (wide.matches) return clear();
    modal.style.setProperty('--etq-vv-h', `${Math.round(vv.height)}px`);
    modal.style.setProperty('--etq-vv-top', `${Math.round(Math.max(0, vv.offsetTop))}px`);
    modal.toggleAttribute('data-etq-kb', vv.scale <= 1.05 && window.innerHeight - vv.height > 120);
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(apply);
  };

  const clear = () => {
    modal.style.removeProperty('--etq-vv-h');
    modal.style.removeProperty('--etq-vv-top');
    modal.removeAttribute('data-etq-kb');
  };

  const open = () => {
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    window.addEventListener('orientationchange', schedule);
    apply();
  };

  const close = () => {
    vv.removeEventListener('resize', schedule);
    vv.removeEventListener('scroll', schedule);
    window.removeEventListener('orientationchange', schedule);
    cancelAnimationFrame(frame);
    frame = 0;
    clear();
  };

  modal.addEventListener('dialog:open', open);
  modal.addEventListener('dialog:close', close);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupSearchViewport);
} else {
  setupSearchViewport();
}
/* ===== end SEARCH MOBILE (2 Nov) ===== */

/* ---------- Add to bag: the ETQ square goes straight to the bag (Melvin 1 Nov) ----------
   Replaces Horizon's fly-to-cart (settings.add_to_cart_animation is ignored in snippets/add-to-cart-button): that one
   started on every click, also without a size, and swung left first. This one only starts on a real add
   (Shopify's cart lines event), from the button that was clicked, in a straight line. */
{
  /** @type {Element | null} */
  let lastAddButton = null;
  document.addEventListener(
    'click',
    (event) => {
      if (event.target instanceof Element) lastAddButton = event.target.closest('button') ?? lastAddButton;
    },
    true
  );

  const flySquare = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const bag = Array.from(document.querySelectorAll('.header-actions__cart-icon')).find((el) => el.getClientRects().length > 0);
    if (!bag || !lastAddButton?.isConnected || lastAddButton.closest('dialog')) return;
    const from = lastAddButton.getBoundingClientRect();
    const to = bag.getBoundingClientRect();
    const x0 = from.left + from.width / 2;
    const y0 = from.top + from.height / 2;
    const x1 = to.left + to.width / 2;
    const y1 = Math.max(to.top + to.height / 2, 24); // header scrolled away: towards the top right corner
    const square = document.createElement('span');
    square.className = 'etq-fly-square';
    square.setAttribute('aria-hidden', 'true');
    document.body.append(square);
    const animation = square.animate(
      [
        { transform: `translate(${x0}px, ${y0}px) translate(-50%, -50%) scale(1)`, opacity: 1 },
        { transform: `translate(${x1}px, ${y1}px) translate(-50%, -50%) scale(0.7)`, opacity: 1 },
      ],
      { duration: 420, easing: 'cubic-bezier(0.45, 0, 0.2, 1)' }
    );
    animation.finished.finally(() => square.remove());
  };

  /* Remembered size (Melvin 1 Nov, as Mr Marvis): a size added to the bag is selected on the next product of the
     same group: shoes, bottoms or tops (data-etq-size-group, snippets/variant-main-picker). Jeans sizes (W32 / L32)
     have their own slot. Only when no size is chosen yet and the size is available. */
  const SIZE_MEMORY = 'etq-size-memory';
  const readSizes = () => {
    try {
      return JSON.parse(localStorage.getItem(SIZE_MEMORY) || '{}') || {};
    } catch {
      return {};
    }
  };
  /** @param {string} group @param {string} value */
  const sizeSlot = (group, value) => (/^W\d/.test(value) ? `${group}-w` : group);

  function rememberSize() {
    const group = document.querySelector('variant-picker[data-etq-size-group]')?.getAttribute('data-etq-size-group');
    if (!group || lastAddButton?.getAttribute('name') !== 'add' || lastAddButton.closest('.etq-added-sheet')) return;
    const checked = mainSizeFieldset()?.querySelector('input:checked');
    if (!(checked instanceof HTMLInputElement)) return;
    try {
      localStorage.setItem(SIZE_MEMORY, JSON.stringify({ ...readSizes(), [sizeSlot(group, checked.value)]: checked.value }));
    } catch {}
  }

  customElements.whenDefined('variant-picker').then(() => {
    const group = document.querySelector('variant-picker[data-etq-size-group]')?.getAttribute('data-etq-size-group');
    const fieldset = mainSizeFieldset();
    if (!group || !fieldset || !document.querySelector('button[name="add"][data-etq-needs-size]')) return;
    const sizes = readSizes();
    const remembered = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
      (input) =>
        input instanceof HTMLInputElement &&
        input.value === sizes[sizeSlot(group, input.value)] &&
        !input.disabled &&
        input.dataset.optionAvailable !== 'false'
    );
    if (remembered instanceof HTMLInputElement) setTimeout(() => remembered.click(), 0);
  });

  import('@shopify/events')
    .then((events) => {
      const name = events?.StandardEvents?.cartLinesUpdate;
      if (!name) return;
      document.addEventListener(name, (event) => {
        if (/** @type {any} */ (event).action === 'add') {
          flySquare();
          rememberSize();
        }
      });
    })
    .catch(() => {});
}
